import React, { useEffect, useMemo, useState } from 'react';
import { Building, LinkedinSquare, World } from '@saleshandy/icons';
import Modal from '../../../../../shared/design-system/components/atoms/modal';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import MultiChipInput from '../../../../../shared/components/multi-chip-input';
import { getCompanyNameByIdsAPI } from '../../../helpers/leads.api';
import { isPositiveCompanyId } from '../../../helpers/helper';

type Config = {
  key: string;
  icon: React.ReactNode;
  labelText: string;
  placeholder: string;
  showExactMatchCheckbox?: boolean;
  maxInputCount?: number;
};

const COMPANY_BULK_SEARCH_CONFIG: Record<string, Config> = {
  company_name: {
    key: 'company_name',
    icon: <Building />,
    labelText: 'Company Name',
    placeholder:
      'Enter list of Company Name separated by commas, semicolon or line breaks.',
    showExactMatchCheckbox: false,
    maxInputCount: 2000,
  },
  company_domain: {
    key: 'company_domain',
    icon: <World />,
    labelText: 'Company Domain',
    placeholder: 'Enter Company Domain',
    showExactMatchCheckbox: false,
    maxInputCount: 2000,
  },
  linkedin_url: {
    key: 'linkedin_url',
    icon: <LinkedinSquare />,
    labelText: 'LinkedIn URL',
    placeholder:
      'Enter LinkedIn URL (e.g. https://www.linkedin.com/in/john-doe) separated by commas, semicolon or line breaks.',
    showExactMatchCheckbox: false,
    maxInputCount: 2000,
  },
};

type Props = {
  show: boolean;
  focusKey?: string;
  pendingFilters: URLSearchParams;
  onClose: () => void;
  onSearch: (params: {
    filterKey: string;
    values: string[];
    showExactMatch: boolean;
  }) => void;
};

const normalize = (v: string) => v.replace(/"/g, '').trim();

const CompanyFilterBulkSearch: React.FC<Props> = ({
  show,
  focusKey,
  pendingFilters,
  onClose,
  onSearch,
}) => {
  const config = useMemo(() => {
    if (!focusKey) return null;
    return COMPANY_BULK_SEARCH_CONFIG[focusKey] ?? null;
  }, [focusKey]);

  const [values, setValues] = useState<string[]>([]);
  const [showExactMatch, setShowExactMatch] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [companyIdNameMap, setCompanyIdNameMap] = useState<
    Record<string, string>
  >({});
  const [isResolvingCompanyIds, setIsResolvingCompanyIds] = useState<boolean>(
    false,
  );

  // Resolve company IDs to names so chips show names (not numeric IDs)
  useEffect(() => {
    if (!show || !config) return;
    if (config.key !== 'company_name') return;

    const ids = pendingFilters
      .getAll('company_ids')
      .map(normalize)
      .filter((v) => v.length > 0)
      .filter((v) => isPositiveCompanyId(v));
    const idsToFetch = ids.filter((id) => !companyIdNameMap[id]);
    if (idsToFetch.length === 0) return;

    let isCancelled = false;
    (async () => {
      setIsResolvingCompanyIds(true);
      try {
        const res = await getCompanyNameByIdsAPI({
          companyIds: idsToFetch.map((id) => Number(id)),
        });
        const payload = (res as any)?.payload;
        if (!Array.isArray(payload)) return;
        const nextMap: Record<string, string> = {};
        payload.forEach((c: any) => {
          const id =
            c?.companyId ?? c?.company_id ?? c?.id ?? c?.companyID ?? null;
          const name =
            c?.companyName ??
            c?.company_name ??
            c?.name ??
            c?.companyNAME ??
            null;
          if (id != null && typeof name === 'string' && name.trim() !== '') {
            nextMap[String(id)] = name.trim();
          }
        });
        if (!isCancelled && Object.keys(nextMap).length > 0) {
          setCompanyIdNameMap((prev) => ({ ...prev, ...nextMap }));
        }
      } catch (_e) {
        // ignore and fall back to IDs
      } finally {
        if (!isCancelled) {
          setIsResolvingCompanyIds(false);
        }
      }
    })();

    // eslint-disable-next-line consistent-return
    return () => {
      isCancelled = true;
    };
  }, [show, config, pendingFilters, companyIdNameMap]);

  useEffect(() => {
    if (!show || !config) return;

    const queryValues = (() => {
      if (config.key !== 'company_name') {
        return pendingFilters.getAll(config.key).map(normalize).filter(Boolean);
      }

      const companyNames = pendingFilters
        .getAll('company_name')
        .map(normalize)
        .filter(Boolean);
      const companyIdsOrNames = pendingFilters
        .getAll('company_ids')
        .map(normalize)
        .filter(Boolean);

      const typedCompanyNames = companyIdsOrNames.filter(
        (v) => !isPositiveCompanyId(v),
      );
      const companyIdsAsNames = companyIdsOrNames
        .filter((v) => isPositiveCompanyId(v))
        .map((id) => companyIdNameMap[id])
        .filter(Boolean) as string[];

      return [
        ...companyNames,
        ...typedCompanyNames,
        ...companyIdsAsNames,
      ].filter((v) => v.length > 0);
    })();

    const exactMatchKeysToRead =
      config.key === 'company_name'
        ? ['company_name_exactMatch', 'company_ids_exactMatch']
        : [`${config.key}_exactMatch`];
    const exactMatch = exactMatchKeysToRead.some(
      (k) => pendingFilters.get(k) === '1',
    );
    setValues(queryValues);
    setShowExactMatch(exactMatch);
    setError('');
  }, [show, config, pendingFilters, companyIdNameMap]);

  const isLimitExceeded = config && values.length > (config.maxInputCount ?? 0);
  const hasAnyValue = values.some((v) => normalize(v).length > 0);

  const onSubmit = () => {
    if (!config || isLimitExceeded) return;
    onSearch({
      filterKey: config.key === 'company_name' ? 'company_ids' : config.key,
      values,
      showExactMatch,
    });
  };

  return (
    <Modal
      show={show}
      titleContent="Bulk Search"
      showCloseIcon={true}
      submitButtonText="Search"
      onHide={onClose}
      onClose={onClose}
      onSubmit={onSubmit}
      isSubmitDisabled={isLimitExceeded || !hasAnyValue}
      className="v2-leads-filter-bulk-search"
    >
      {config ? (
        <div key={config.key}>
          <div className="search-input-label-container">
            <div className="search-input-label">
              <div className="label-icon">{config.icon}</div>
              <div className="label">{config.labelText}</div>
            </div>
            {config.showExactMatchCheckbox ? (
              <Checkbox
                value={config.key}
                className="show-exact-match-check"
                onChange={(checked) => setShowExactMatch(checked)}
                checked={showExactMatch}
                disabled={values.length === 0}
                label={
                  <div className="label-container d-inline-block">
                    <p className="checkbox-label-text m-0">Show Exact Match</p>
                  </div>
                }
              />
            ) : null}
          </div>
          <MultiChipInput
            values={values}
            onChange={setValues}
            error={error}
            onError={setError}
            placeholder={config.placeholder}
            showInputCount={true}
            maxInputCount={config.maxInputCount}
            isLoading={config.key === 'company_name' && isResolvingCompanyIds}
            scrollTo={true}
          />
        </div>
      ) : null}
    </Modal>
  );
};

export default CompanyFilterBulkSearch;
